import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut } from "lucide-react";

interface ImageCropModalProps {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  onCropComplete: (croppedImage: File) => void;
  cropShape?: "rect" | "round";
}

interface CroppedArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (imageSrc: string, pixelCrop: CroppedArea, isCircular: boolean = false): Promise<Blob> => {
  const image = await createImage(imageSrc);
  
  // Target dimensions - circular for avatars, 16:9 for macro cards
  const TARGET_WIDTH = isCircular ? 512 : 1920;
  const TARGET_HEIGHT = isCircular ? 512 : 1080;
  
  // First canvas: crop the image
  const cropCanvas = document.createElement("canvas");
  const cropCtx = cropCanvas.getContext("2d");

  if (!cropCtx) {
    throw new Error("No 2d context");
  }

  // Round pixel values to avoid sub-pixel rendering issues
  const x = Math.round(pixelCrop.x);
  const y = Math.round(pixelCrop.y);
  const width = Math.round(pixelCrop.width);
  const height = Math.round(pixelCrop.height);

  cropCanvas.width = width;
  cropCanvas.height = height;

  cropCtx.drawImage(
    image,
    x,
    y,
    width,
    height,
    0,
    0,
    width,
    height
  );

  // Second canvas: resize to target dimensions
  const resizeCanvas = document.createElement("canvas");
  const resizeCtx = resizeCanvas.getContext("2d");
  
  if (!resizeCtx) {
    throw new Error("No 2d context for resize");
  }

  resizeCanvas.width = TARGET_WIDTH;
  resizeCanvas.height = TARGET_HEIGHT;

  // Use high-quality image smoothing
  resizeCtx.imageSmoothingEnabled = true;
  resizeCtx.imageSmoothingQuality = "high";

  // If circular, clip to circle
  if (isCircular) {
    resizeCtx.beginPath();
    resizeCtx.arc(TARGET_WIDTH / 2, TARGET_HEIGHT / 2, TARGET_WIDTH / 2, 0, Math.PI * 2);
    resizeCtx.closePath();
    resizeCtx.clip();
  }

  // Draw the cropped image onto the resize canvas at target dimensions
  resizeCtx.drawImage(
    cropCanvas,
    0,
    0,
    width,
    height,
    0,
    0,
    TARGET_WIDTH,
    TARGET_HEIGHT
  );

  return new Promise((resolve, reject) => {
    resizeCanvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas is empty"));
        return;
      }
      resolve(blob);
    }, "image/png");
  });
};

export const ImageCropModal = ({ open, onClose, imageSrc, onCropComplete, cropShape = "rect" }: ImageCropModalProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.5);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedArea | null>(null);

  const onCropChange = (location: { x: number; y: number }) => {
    setCrop(location);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropCompleteCallback = useCallback(
    (_croppedArea: any, croppedAreaPixels: CroppedArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels) return;

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, cropShape === "round");
      const croppedFile = new File([croppedBlob], cropShape === "round" ? "avatar.png" : "screenshot.png", {
        type: "image/png",
      });
      onCropComplete(croppedFile);
      onClose();
    } catch (e) {
      console.error("Error cropping image:", e);
    }
  };

  const handleCancel = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(0.5);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="max-w-4xl bg-background border-border">
        <DialogHeader>
          <DialogTitle>Crop Image</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative w-full h-[500px] bg-muted rounded-lg overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={cropShape === "round" ? 1 : 16 / 9}
              cropShape={cropShape}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              restrictPosition={false}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <ZoomOut className="w-4 h-4 text-muted-foreground" />
              <Slider
                value={[zoom]}
                onValueChange={(value) => setZoom(value[0])}
                min={0.3}
                max={3}
                step={0.01}
                className="flex-1"
              />
              <ZoomIn className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Drag to reposition • Scroll or use slider to zoom • Output: {cropShape === "round" ? "512x512" : "1920x1080"}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSaveCrop}>
            Save Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
